import React from 'react';
import ReactDOM from 'react-dom';
import { 
  Box, 
  IconButton, 
  Backdrop, 
  Fade, 
  Typography,
  Toolbar,
  AppBar,
  Stack  
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { styled } from '@mui/material/styles';

const ViewerContainer = styled(Box)(() => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 99999,
}));

const ImageContainer = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  overflow: 'hidden',
  padding: '16px',
}));

interface StyledImageProps {
  zoom: number;
}

const StyledImage = styled('img')<StyledImageProps>(() => ({
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
  transition: 'transform 0.3s ease',
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  backgroundColor: 'rgba(0, 0, 0, 0)',
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0)',
  },
}));

interface ImageViewerProps {
  open: boolean;
  onClose: () => void;
  imageUrl: string;
  alt?: string;
  hasNavigation?: boolean;
  onNext?: () => void;
  onPrevious?: () => void;
  title?: string;
}

const ImageViewer: React.FC<ImageViewerProps> = ({ 
  open, 
  onClose, 
  imageUrl, 
  alt = 'Image',
  hasNavigation = false,
  onNext,
  onPrevious,
  title
}) => {
  if (!open) return null;

  return ReactDOM.createPortal(
    <Backdrop 
      open={open} 
      onClick={onClose}
      sx={{ 
        zIndex: 99999,
        backgroundColor: 'rgba(0, 0, 0, 0)',
      }}
    >
      <Fade in={open}>
        <ViewerContainer onClick={(e) => e.stopPropagation()}>
          <AppBar position="static" color="transparent" elevation={0}>
            <Toolbar>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'white' }}>
                {title || alt || 'Image Viewer'}
              </Typography>
              <Stack direction="row" spacing={1}>
                <IconButton color="inherit" onClick={onClose} edge="end">
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Toolbar>
          </AppBar>

          <ImageContainer>
            <StyledImage 
              src={imageUrl} 
              alt={alt} 
              zoom={1}
              onClick={(e) => e.stopPropagation()} 
            />
            
            {hasNavigation && (
              <>
                <NavigationButton 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onPrevious) onPrevious();
                  }}
                  sx={{ left: 16 }}
                  size="large"
                >
                  <NavigateBeforeIcon fontSize="large" />
                </NavigationButton>
                
                <NavigationButton 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onNext) onNext();
                  }}
                  sx={{ right: 16 }}
                  size="large"
                >
                  <NavigateNextIcon fontSize="large" />
                </NavigationButton>
              </>
            )}
          </ImageContainer>
        </ViewerContainer>
      </Fade>
    </Backdrop>,
    document.body
  );
};

export default ImageViewer;